import { GallerySku } from 'domains/gallerySkuDomain';
import { useTranslation } from 'react-i18next';
import {
  isMostOrdered,
  isRotatedMatch,
  matchedFlatSizeLabel,
  matchedSizeLabel,
  wasRankedByDimension,
} from 'utils/skuSizeMatch';
import './SkuMatchChips.less';

/**
 * Why a SKU is where it is in a Canva-ranked gallery: the size it was ranked on, and how often
 * this org has ordered it.
 *
 * Renders nothing outside the Canva flow. The API only fills these fields when the request carried
 * the parked design's size, so `sizeMatch` being absent is precisely "this is a normal gallery
 * visit" — see `wasRankedByDimension`. That keeps the ordinary gallery exactly as it was.
 *
 * The size badge sits on every SKU that fits; "Most ordered" sits on exactly one in the whole
 * catalogue. Both can land on the same SKU, which is why they are separate badges rather than one
 * slot: a product that both fits the design and is what everyone orders deserves to say so twice.
 */
export const SkuMatchChips = (props: { sku?: GallerySku }) => {
  const { sku } = props;
  const { t } = useTranslation();

  if (!sku || !wasRankedByDimension(sku.sizeMatch)) {
    return null;
  }

  // Only an actual fit earns a size badge. A SKU that merely came closest carries no badge -- its
  // closeness is expressed by where the ranking puts it, not by a label claiming a near-match.
  const matched = matchedSizeLabel(sku);
  // The sheet the match was actually computed on, when the product folds down from a larger one.
  // Naming only the finished size makes a correct match on a folded product look wrong.
  const matchedFlat = matchedFlatSizeLabel(sku);
  // A superlative over the whole catalogue, not a per-row property, so it comes from the server
  // rather than being derived from the items on screen.
  const mostOrdered = isMostOrdered(sku);

  if (!matched && !mostOrdered) {
    return null;
  }

  return (
    <div className="sku-match-chips">
      {matched && (
        <span
          className="sku-match-chips_chip sku-match-chips_chip--match"
          title={
            isRotatedMatch(sku.sizeMatch)
              ? t('gallery.canvaMatch.rotatedHint')
              : undefined
          }
        >
          {matchedFlat
            ? t('gallery.canvaMatch.matchingFlatDimensions', {
                flatSize: matchedFlat,
                size: matched,
              })
            : t('gallery.canvaMatch.matchingDimensions', { size: matched })}
        </span>
      )}
      {mostOrdered && (
        <span
          className="sku-match-chips_chip sku-match-chips_chip--ordered"
          title={t('gallery.canvaMatch.mostOrderedHint')}
        >
          {t('gallery.canvaMatch.mostOrdered')}
        </span>
      )}
    </div>
  );
};
